import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState, ɵPendingTasks as PendingTasks } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import resolveResponse from 'contentful-resolve-response';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, from, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments';
import { SeoService } from './seo.service';
// import { TypeJobListingFields } from './content-types';

@Injectable({
  providedIn: 'root'
})
export class ContentService {

  private filtersSubject = new BehaviorSubject<any>(null)
  public filters = this.filtersSubject.asObservable().pipe(distinctUntilChanged())
  private filtersLocal: any[]
  
  // contentful's content delivery api, called directly (replaces the contentful sdk).
  // HttpBackend skips the app's http interceptors (auth headers, angular's transfer cache):
  // these requests go to contentful, not our api, and TransferState is handled in request()
  private http = new HttpClient(inject(HttpBackend))
  private apiUrl = `https://cdn.contentful.com/spaces/${environment.contentful.spaceId}/environments/${environment.contentful.environment || 'master'}`

  private fetchJson(path: string, query: any = {}): Promise<any> {
    const params = new HttpParams({ fromObject: Object.fromEntries(Object.entries(query).map(([k, v]) => [k, String(v)])) })
    const headers = { Authorization: `Bearer ${environment.contentful.accessToken}` }
    return firstValueFrom(this.http.get(this.apiUrl + path, { params, headers }))
  }

  private isServer = isPlatformServer(inject(PLATFORM_ID))
  private pendingTasks = inject(PendingTasks)
  private transferState = inject(TransferState)
  private seoService = inject(SeoService)

  constructor() {}

  // all contentful requests go through here, for pre-rendering:
  // - on the server, the request is a pending task (so rendering waits for it) and the raw
  //   response is saved in TransferState, which is embedded in the pre-rendered page
  // - in the browser, a saved response is returned synchronously instead of refetching, so the
  //   first render matches the pre-rendered html and hydrates without flicker
  // links are resolved here (as the contentful sdk would) so both sides get identical data
  private request(name: string, query: any, fetchRaw: () => Promise<any>, resolve = (raw: any) => raw): Observable<any> {
    const key = makeStateKey<any>(`contentful:${name}:${JSON.stringify(query)}`)
    if (!this.isServer && this.transferState.hasKey(key)) {
      return of(resolve(this.transferState.get(key, null)))
    }
    const taskId = this.isServer ? this.pendingTasks.add() : null
    const promise = fetchRaw().then(raw => {
      if (this.isServer) this.transferState.set(key, raw)
      return resolve(raw)
    })
    promise.catch(() => {}).finally(() => { if (taskId !== null) this.pendingTasks.remove(taskId) })
    return from(promise)
  }

  private getEntries(query: any): Observable<any> {
    return this.request('entries', query,
      () => this.fetchJson('/entries', query),
      raw => Object.assign({}, raw, { items: resolveResponse(raw, { removeUnresolved: false, itemEntryPoints: ['fields'] }) })
    )
  }

  getProjects(query?: object){
    // console.log('getProjects', query)
    return from(
      this.getEntries(Object.assign({
      content_type: 'project'
      }, query))
    );
  }

  getClients(query?: object){
    // console.log('getProjects', query)
    return from(
      this.getEntries(Object.assign({
      content_type: 'client'
      }, query))
    );
  }

  getProjectBySlug(slug: string) {
    let projects = this.getProjectsBySlug(slug)
    console.log('TODO: gpbs', slug, projects)
    // return projects[0]
  }
  // the project page loads its project through here (drafts get noindex, see noindexDrafts)
  getProjectsBySlug(slug: string, query?: any){
    return from(
      this.getEntries(Object.assign({
      content_type: 'project',
      'fields.slug': slug,
      }, query))
    ).pipe(this.noindexDrafts())
  }

  // every page component loads its own page through here (drafts get noindex, see noindexDrafts)
  getPage(query?: object){
    // console.log('GET PAGE', query)
    return from(
      this.getEntries(Object.assign({
        //      this.client.getEntries<TypeJobListingFields>(Object.assign({
        content_type: 'homepage', // NOT A TYPO; Page is locked with ID 'homepage'
        include: 10
      }, query))
    ).pipe(this.noindexDrafts())
  }

  // pages and projects marked Draft in contentful get noindex; they're also left out of the
  // sitemap and not pre-rendered (scripts/site-routes.mjs)
  private noindexDrafts() {
    return tap((content: any) => { if (content?.items?.[0]?.fields?.['draft']) this.seoService.update({ noindex: true }) })
  }
  
  getMainNavItems(query?: object) {
    return from(
      this.getEntries(Object.assign({
        //      this.client.getEntries<TypeJobListingFields>(Object.assign({
        content_type: 'menu',
        'fields.slug': 'main-nav',
      }, query))
    )
  }

  getFilters(query?: object){
    return from(
      this.getEntries(Object.assign({
      //      this.client.getEntries<TypeJobListingFields>(Object.assign({
      content_type: 'filter'
      }, query))
    );
  }

  getMetadata(query?: object){
    // console.log('getMetadata', query)
    return from(
      this.getEntries(Object.assign({
      content_type: 'metadata'
      }, query))
    );
  }

  getTags(query?: object) {
    return from(
      this.request('tags', {}, () => this.fetchJson('/tags').catch(console.error))
    )
  }
}